import React from "react";

import { GuidedTour, TourStep } from "@shared/tour/GuidedTour";

/**
 * First-visit guided tour for the Network Topology product (CTX-97 launch
 * inside Reflector V2). Four short steps: welcome, source browser, canvas
 * interactions, search + detail panel.
 *
 * Since CTX-510 this is only the content. The mechanism — the spotlight, the
 * centered-card fallback when an anchor is not mounted, the deferred
 * re-measure, the one-shot storage key — lives in `GuidedTour`, which the
 * What's new walkthrough drives with its own steps.
 */
export const TOUR_STORAGE_KEY = "ctx-network-topology-tour-v1";

const STEPS: TourStep[] = [
	{
		id: "welcome",
		title: "Meet Network Topology",
		body: "A live, source-first map of your cloud networks — VPCs, availability zones, subnets, ENIs and the gateways that connect them, exactly as your Reflectors observe them.",
	},
	{
		id: "sources",
		title: "Pick a source",
		body: "Browse your authorized Reflectors by cloud, account and region. Selecting one loads its latest observed topology.",
		selector: '[data-testid="scope-browser-tree"]',
	},
	{
		id: "canvas",
		title: "Explore the canvas",
		body: "Click a VPC card to open it — route tables come first, then availability zones. Open several VPCs at once; each card has its own collapse control, and the connectivity tray above a VPC shows the gateways attached to it.",
		selector: ".react-flow",
	},
	{
		id: "inspect",
		title: "Inspect anything",
		body: "Click any element — a route table, gateway, subnet or ENI — to inspect its routes, gateways and attributes in the side panel. The List view shows the same topology as a sortable table.",
	},
];

export const NetworkTopologyTour: React.FC = () => (
	<GuidedTour
		steps={STEPS}
		storageKey={TOUR_STORAGE_KEY}
		testId="network-topology-tour"
		ariaLabel="Network Topology tour"
		eyebrow="New product"
	/>
);
